import { useSyncExternalStore } from 'react';
import { useIsSleek } from '../theme';

/**
 * A yes/no the player decides for themselves and keeps across sessions, in
 * localStorage: off until they say otherwise. Storage that is blocked (a
 * private tab, a strict embed) turns it into a choice for this page only.
 */
export interface PlayerPreference {
  get(): boolean;
  set(next: boolean): void;
  subscribe(cb: () => void): () => void;
  /** Tests only. */
  reset(): void;
}

export function createPlayerPreference(key: string): PlayerPreference {
  let value: boolean | null = null;
  const listeners = new Set<() => void>();

  const get = () => {
    if (value !== null) return value;
    try {
      value = window.localStorage.getItem(key) === '1';
    } catch {
      value = false;
    }
    return value;
  };

  return {
    get,
    set(next) {
      if (get() === next) return;
      value = next;
      try {
        window.localStorage.setItem(key, next ? '1' : '0');
      } catch {
        // Storage blocked — the choice lives for this page only
      }
      for (const cb of listeners) cb();
    },
    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    reset() {
      value = null;
    },
  };
}

export function usePlayerPreference(preference: PlayerPreference): boolean {
  return useSyncExternalStore(preference.subscribe, preference.get, () => false);
}

/**
 * The player would rather see the card than the ceremony: a library mechanic
 * opens the pack without its scene, straight to the winner.
 */
export const skipAnimation = createPlayerPreference('gp_skip_animation');

export function useSkipAnimation(): boolean {
  const stored = usePlayerPreference(skipAnimation);
  const isSleek = useIsSleek();
  // Sleek has no skip switch (partner mockup: one swipe, no skipping), so a
  // choice stored under another template must not silently apply there.
  return stored && !isSleek;
}
